import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Paper,
  Chip,
  IconButton,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Alert,
  alpha,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { usePermissions } from '../../../../utils/permissions';
import CreateRoleDialogue from './roles-and-permissions-components/CreateRoleDialogue';

export default function RolesAndPermissionsTab() {
  const theme = useTheme();
  const { canEditPage, canAccessPage, canDeletePage, hasPermission, companyId, hierarchyLevel } = usePermissions();
  
  // Main state
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggingRole, setDraggingRole] = useState(null);
  const [customLevels, setCustomLevels] = useState(() => {
    const saved = localStorage.getItem('customHierarchyLevels');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Filter state
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [selectedHierarchyLevel, setSelectedHierarchyLevel] = useState('');
  const [expandedLevels, setExpandedLevels] = useState(() => {
    const saved = localStorage.getItem('userManagement_expandedLevels');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.error('Error parsing expanded levels from localStorage:', error);
      }
    }
    return {};
  });
  
  // UI states
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
  const [addLevelDialogOpen, setAddLevelDialogOpen] = useState(false);
  const [newLevelNumber, setNewLevelNumber] = useState('');
  const [deleteLevelDialogOpen, setDeleteLevelDialogOpen] = useState(false);
  const [levelToDelete, setLevelToDelete] = useState(null);
  const [usersAffectedByLevelDeletion, setUsersAffectedByLevelDeletion] = useState([]);

  const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    can_access_settings: false,
    permissions: {},
  });
  const [confirmDeleteRoleDialogOpen, setConfirmDeleteRoleDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [usersAffectedByRoleDeletion, setUsersAffectedByRoleDeletion] = useState([]);

  useEffect(() => {
    if (companyId) {
      loadRoles();
      loadPermissions();
    }
  }, [companyId]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const { data: rolesData, error } = await supabase
        .from('roles')
        .select('*')
        .or(`company_id.eq.${companyId},is_system_role.eq.true`)
        .order('hierarchy_level', { ascending: true });

      if (error) throw error;
      
      // Load permissions for each role to check settings access
      const rolesWithPermissions = [];
      for (const role of rolesData || []) {
        try {
          const { data: rolePermissions, error: permError } = await supabase
            .from('role_permissions')
            .select(`
              can_read,
              permissions!inner (
                name,
                category,
                resource
              )
            `)
            .eq('role_id', role.id)
            .eq('can_read', true);

          if (permError) {
            console.error('Error loading permissions for role:', role.id, permError);
            rolesWithPermissions.push(role);
            continue;
          }

          // Check if role has settings view permission
          const hasSettingsAccess = rolePermissions?.some(rp => 
            rp.permissions.category === 'settings' || 
            rp.permissions.resource === 'settings' ||
            rp.permissions.resource === 'company_settings' ||
            rp.permissions.resource === 'users'
          ) || role.can_access_settings;

          rolesWithPermissions.push({
            ...role,
            hasSettingsViewPermission: hasSettingsAccess
          });
        } catch (err) {
          console.error('Error processing role permissions:', err);
          rolesWithPermissions.push(role);
        }
      }
      
      setRoles(rolesWithPermissions);
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const { data: permissionsData, error } = await supabase
        .from('permissions')
        .select('*')
        .order('category', { ascending: true });

      if (error) throw error;
      setPermissions(permissionsData || []);
    } catch (error) {

    }
  };



  const handleDragStart = (e, role) => {
    setDraggingRole(role);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetLevel) => {
    e.preventDefault();
    if (!draggingRole) return;

    // Prevent dropping into levels at or above user's hierarchy level
    if (targetLevel <= hierarchyLevel) {
      setDraggingRole(null);
      return;
    }

    // Prevent dropping roles from levels at or above user's hierarchy level
    if (draggingRole.hierarchy_level <= hierarchyLevel) {
      setDraggingRole(null);
      return;
    }

    try {
      const { error } = await supabase
        .from('roles')
        .update({ hierarchy_level: targetLevel })
        .eq('id', draggingRole.id);

      if (error) throw error;

      // If moving to a custom level, ensure it stays in customLevels
      if (customLevels.includes(targetLevel)) {
        const updatedCustomLevels = [...new Set([...customLevels, targetLevel])];
        setCustomLevels(updatedCustomLevels);
        localStorage.setItem('customHierarchyLevels', JSON.stringify(updatedCustomLevels));
      }

      setDraggingRole(null);
      loadRoles();
    } catch (error) {

      setDraggingRole(null);
    }
  };

  const createNewHierarchyLevel = () => {
    setAddLevelDialogOpen(true);
    setNewLevelNumber('');
  };

  const handleCreateLevel = () => {
    const levelNum = parseInt(newLevelNumber);
    if (!levelNum || levelNum <= hierarchyLevel) {

      return;
    }

    const allLevels = [...new Set([...roles.map(r => r.hierarchy_level), ...customLevels])];
    if (allLevels.includes(levelNum)) {
      return;
    }

    const newCustomLevels = [...customLevels, levelNum];
    setCustomLevels(newCustomLevels);
    localStorage.setItem('customHierarchyLevels', JSON.stringify(newCustomLevels));


    setAddLevelDialogOpen(false);
    setNewLevelNumber('');
  };

  const doesLevelExist = () => {
    const levelNum = parseInt(newLevelNumber);
    if (!levelNum) return false;
    const allLevels = [...new Set([...roles.map(r => r.hierarchy_level), ...customLevels])];
    return allLevels.includes(levelNum);
  };

  const isInvalidLevelNumber = () => {
    const levelNum = parseInt(newLevelNumber);
    return levelNum <= 0;
  };

  const handleDeleteLevelClick = async (level) => {
    try {
      const rolesInLevel = roles.filter(role => role.hierarchy_level === level);
      if (rolesInLevel.length > 0) {
        const usersWithRoles = [];
        for (const role of rolesInLevel) {
          const { data: usersWithRole } = await supabase
            .from('company_users')
            .select('id, user_id')
            .eq('role_id', role.id)
            .eq('status', 'active');
          if (usersWithRole && usersWithRole.length > 0) {
            for (const user of usersWithRole) {
              try {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('first_name, last_name, full_name')
                  .eq('id', user.user_id)
                  .single();
                usersWithRoles.push({
                  ...user,
                  role_name: role.display_name,
                  profiles: profile || { first_name: '', last_name: '', full_name: 'Unknown User' }
                });
              } catch (profileError) {
                usersWithRoles.push({
                  ...user,
                  role_name: role.display_name,
                  profiles: { first_name: '', last_name: '', full_name: 'Unknown User' }
                });
              }
            }
          }
        }
        setUsersAffectedByLevelDeletion(usersWithRoles);
      } else {
        setUsersAffectedByLevelDeletion([]);
      }
      setLevelToDelete(level);
      setDeleteLevelDialogOpen(true);
    } catch (error) {
      setUsersAffectedByLevelDeletion([]);
      setLevelToDelete(level);
      setDeleteLevelDialogOpen(true);
    }
  };

  const handleDeleteLevel = async () => {
    try {
      if (!levelToDelete) return;
      const rolesInLevel = roles.filter(role => role.hierarchy_level === levelToDelete);
      if (rolesInLevel.length > 0) {
        return;
      }
      const newCustomLevels = customLevels.filter(l => l !== levelToDelete);
      setCustomLevels(newCustomLevels);
      localStorage.setItem('customHierarchyLevels', JSON.stringify(newCustomLevels));
      setDeleteLevelDialogOpen(false);
      setLevelToDelete(null);
      loadRoles();
    } catch (error) {
    }
  };

  const handleCreateRole = () => {
    setCreateRoleDialogOpen(true);
  };

  const handleConfirmCreateRole = async (roleData) => {
    try {
      // Get the next available hierarchy level below current user
      const existingLevels = [...new Set([...roles.map(r => r.hierarchy_level), ...customLevels])];
      const nextLevel = hierarchyLevel + 1;

      // Create the role
      const { data: newRole, error: roleError } = await supabase
        .from('roles')
        .insert({
          name: roleData.name,
          display_name: roleData.display_name,
          description: roleData.description,
          company_id: companyId,
          hierarchy_level: nextLevel,
          is_system_role: false
        })
        .select()
        .single();

      if (roleError) throw roleError;

      // Create role permissions
      if (roleData.permissions && Object.keys(roleData.permissions).length > 0) {
        const rolePermissions = Object.entries(roleData.permissions)
          .filter(([_, perms]) => perms.read || perms.write || perms.delete)
          .map(([permissionId, perms]) => ({
            role_id: newRole.id,
            permission_id: permissionId,
            can_read: perms.read || false,
            can_write: perms.write || false,
            can_delete: perms.delete || false
          }));

        if (rolePermissions.length > 0) {
          const { error: permError } = await supabase
            .from('role_permissions')
            .insert(rolePermissions);

          if (permError) throw permError;
        }
      }

      loadRoles();
      setCreateRoleDialogOpen(false);
    } catch (error) {
      throw error;
    }
  };

  const loadRoleForEdit = (role) => {
    setRoleToEdit(role);
    setEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async (roleId, roleData) => {
    try {
      // Update the role basic info
      const { error: roleError } = await supabase
        .from('roles')
        .update({
          display_name: roleData.display_name,
          description: roleData.description
        })
        .eq('id', roleId);

      if (roleError) throw roleError;

      // Delete existing permissions
      const { error: deleteError } = await supabase
        .from('role_permissions')
        .delete()
        .eq('role_id', roleId);

      if (deleteError) throw deleteError;

      // Create new role permissions
      if (roleData.permissions && Object.keys(roleData.permissions).length > 0) {
        const rolePermissions = Object.entries(roleData.permissions)
          .filter(([_, perms]) => perms.read || perms.write || perms.delete)
          .map(([permissionId, perms]) => ({
            role_id: roleId,
            permission_id: permissionId,
            can_read: perms.read || false,
            can_write: perms.write || false,
            can_delete: perms.delete || false
          }));

        if (rolePermissions.length > 0) {
          const { error: permError } = await supabase
            .from('role_permissions')
            .insert(rolePermissions);

          if (permError) throw permError;
        }
      }

      loadRoles();
      setEditRoleDialogOpen(false);
      setRoleToEdit(null);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteRoleClick = async (role) => {
    try {
      const { data: usersWithRole, error: usersError } = await supabase
        .from('company_users')
        .select('id, user_id')
        .eq('role_id', role.id)
        .eq('status', 'active');
      if (usersError) {
        setUsersAffectedByRoleDeletion([]);
      } else {
        const usersWithProfiles = [];
        if (usersWithRole && usersWithRole.length > 0) {
          for (const user of usersWithRole) {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('first_name, last_name, full_name')
                .eq('id', user.user_id)
                .single();
              usersWithProfiles.push({
                ...user,
                profiles: profile || { first_name: '', last_name: '', full_name: 'Unknown User' }
              });
            } catch (profileError) {
              usersWithProfiles.push({
                ...user,
                profiles: { first_name: '', last_name: '', full_name: 'Unknown User' }
              });
            }
          }
        }
        setUsersAffectedByRoleDeletion(usersWithProfiles);
      }
      setRoleToDelete(role);
      setConfirmDeleteRoleDialogOpen(true);
    } catch (error) {
      setUsersAffectedByRoleDeletion([]);
      setRoleToDelete(role);
      setConfirmDeleteRoleDialogOpen(true);
    }
  };

  const handleDeleteRole = async () => {
    try {
      if (!roleToDelete) return;
      
      // First delete all user invitations using this role
      const { error: invitationsError } = await supabase
        .from('user_invitations')
        .delete()
        .eq('role_id', roleToDelete.id);
      
      if (invitationsError) {
        console.error('Error deleting user invitations:', invitationsError);
        // Continue anyway
      }
      
      // Then delete all role permissions
      const { error: permError } = await supabase
        .from('role_permissions')
        .delete()
        .eq('role_id', roleToDelete.id);
      
      if (permError) {
        console.error('Error deleting role permissions:', permError);
        // Continue anyway, might not exist
      }
      
      // Finally delete the role
      const { error: roleError } = await supabase
        .from('roles')
        .delete()
        .eq('id', roleToDelete.id);
        
      if (roleError) {
        console.error('Error deleting role:', roleError);
        throw roleError;
      }
      
      setConfirmDeleteRoleDialogOpen(false);
      setRoleToDelete(null);
      loadRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
    }
  };

  const getRoleStatusColor = (hierarchyLevel) => {
    switch (hierarchyLevel) {
      case 1: return 'error';
      case 2: return 'warning';
      case 3: return 'info';
      case 4: return 'success';
      default: return 'default';
    }
  };

  const getHierarchyLevelName = (level) => {
    switch (level) {
      case 1: return 'Super Administrator Level';
      default: return `Level ${level}`;
    }
  };

  const toggleLevelExpansion = (level) => {
    setExpandedLevels(prev => {
      const newState = {
        ...prev,
        [level]: !prev[level]
      };
      // Save to localStorage
      localStorage.setItem('userManagement_expandedLevels', JSON.stringify(newState));
      return newState;
    });
  };

  const getRolesByHierarchy = () => {
    const grouped = {};
    roles.forEach(role => {
      if (!grouped[role.hierarchy_level]) {
        grouped[role.hierarchy_level] = [];
      }
      grouped[role.hierarchy_level].push(role);
    });
    return grouped;
  };

  const getFilteredRolesByHierarchy = () => {
    const grouped = getRolesByHierarchy();
    
    // Filter by hierarchy level first
    const filteredGrouped = {};
    Object.entries(grouped).forEach(([level, levelRoles]) => {
      if (!selectedHierarchyLevel || level === selectedHierarchyLevel) {
        filteredGrouped[level] = levelRoles;
      }
    });
    
    // Then filter by search term
    if (roleSearchTerm && roleSearchTerm.trim()) {
      const searchTerm = roleSearchTerm.toLowerCase().trim();
      
      Object.entries(filteredGrouped).forEach(([level, levelRoles]) => {
        const matchingRoles = levelRoles.filter(role => {
          const name = (role.name || '').toLowerCase();
          const displayName = (role.display_name || '').toLowerCase();
          const description = (role.description || '').toLowerCase();
          
          // Handle common abbreviations
          const normalizedSearchTerm = searchTerm
            .replace(/administrator/g, 'admin')
            .replace(/administration/g, 'admin');
          
          const normalizedName = name
            .replace(/administrator/g, 'admin')
            .replace(/administration/g, 'admin');
          
          const normalizedDisplayName = displayName
            .replace(/administrator/g, 'admin')
            .replace(/administration/g, 'admin');
          
          return name.includes(searchTerm) || 
                 displayName.includes(searchTerm) || 
                 description.includes(searchTerm) ||
                 name.includes(normalizedSearchTerm) ||
                 displayName.includes(normalizedSearchTerm) ||
                 normalizedName.includes(searchTerm) ||
                 normalizedDisplayName.includes(searchTerm) ||
                 name.replace(/\s+/g, '').includes(searchTerm.replace(/\s+/g, '')) ||
                 displayName.replace(/\s+/g, '').includes(searchTerm.replace(/\s+/g, ''));
        });
        
        if (matchingRoles.length > 0) {
          filteredGrouped[level] = matchingRoles;
        } else {
          delete filteredGrouped[level];
        }
      });
    }
    
    return filteredGrouped;
  };

  const clearFilters = () => {
    setRoleSearchTerm('');
    setSelectedHierarchyLevel('');
  };

  const hasRolesInLevel = (level) => {
    return roles.filter(role => role.hierarchy_level === level).length > 0;
  };

  if (!canAccessPage('roles_permissions')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Roles & Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to view roles and permissions. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <>
    <Box sx={{ display: 'flex', height: '75vh', gap: 3, px: 3, py: 3, overflow: 'hidden' }}>
      {alert.show && (
        <Alert 
          severity={alert.severity} 
          sx={{ 
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            borderRadius: 3 
          }}
          onClose={() => setAlert({ show: false, message: '', severity: 'info' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* Hierarchy Sidebar */}
      <Paper sx={{ 
        width: '280px',
        minWidth: '280px',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', mb: 1, fontSize: '1rem' }}>
            Hierarchy Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.8rem' }}>
            Drag roles to rearrange hierarchy levels
          </Typography>
          {canEditPage('roles_permissions') && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={createNewHierarchyLevel}
              sx={{
                fontSize: '0.7rem',
                px: 1.5,
                py: 0.4,
                borderRadius: 3,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              Add Level
            </Button>
          )}
        </Box>
        
        <Box sx={{ 
          flex: 1,
          p: 1.5, 
          pb: 3,
          overflowY: 'auto',
          maxHeight: 'calc(100% - 80px)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.grey[300], 0.3),
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.grey[500], 0.6),
            borderRadius: '3px',
            '&:hover': {
              background: alpha(theme.palette.grey[600], 0.8)
            }
          }
        }}>
          {[...new Set([...roles.map(role => role.hierarchy_level), ...customLevels])]
            .sort((a, b) => a - b)
            .map(level => (
              <Box 
                key={level} 
                sx={{ mb: 1.5 }}
                onDragOver={level > hierarchyLevel && canEditPage('roles_permissions') ? handleDragOver : undefined}
                onDrop={level > hierarchyLevel && canEditPage('roles_permissions') ? (e) => handleDrop(e, level) : undefined}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  background: level === 1 ? 
                    `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.main, 0.05)})` :
                    `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: level === 1 ? 
                    `1px solid ${alpha(theme.palette.error.main, 0.3)}` :
                    `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  minHeight: 20,
                  transition: 'all 0.2s ease',
                  '&:hover': level > hierarchyLevel ? {
                    background: level === 1 ? 
                      `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.15)}, ${alpha(theme.palette.error.main, 0.08)})` :
                      `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                    border: level === 1 ? 
                      `1px solid ${alpha(theme.palette.error.main, 0.4)}` :
                      `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
                  } : {}
                }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 500, 
                    color: level === 1 ? 'error.main' : 'primary.main',
                    fontSize: '0.75rem'
                  }}>
                    Level {level}
                  </Typography>
                  
                  {customLevels.includes(level) && level > hierarchyLevel && canDeletePage('roles_permissions') && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLevelClick(level);
                      }}
                      sx={{
                        p: 0.3,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.15),
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <DeleteIcon sx={{ color: 'error.main', fontSize: '0.8rem' }} />
                    </IconButton>
                  )}
                </Box>
                
                <Box sx={{ ml: 1.5 }}>
                  {roles.filter(role => role.hierarchy_level === level).length === 0 && customLevels.includes(level) ? (
                    <Paper sx={{
                      p: 1,
                      mb: 0.5,
                      borderRadius: 2,
                      border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
                      background: alpha(theme.palette.warning.main, 0.05),
                      textAlign: 'center',
                      minHeight: 28
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: 'warning.main',
                        fontWeight: 500,
                        fontSize: '0.7rem'
                      }}>
                        Ready for new roles
                      </Typography>
                    </Paper>
                  ) : (
                    roles
                      .filter(role => role.hierarchy_level === level)
                      .map(role => (
                      <Paper
                        key={role.id}
                        draggable={!role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions')}
                        onDragStart={(e) => !role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions') && handleDragStart(e, role)}
                        onDragEnd={() => setDraggingRole(null)}
                        sx={{
                          p: 1.5,
                          mb: 0.8,
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          cursor: !role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions') ? 'grab' : 'default',
                          opacity: draggingRole?.id === role.id ? 0.5 : 1,
                          background: 'white',
                          userSelect: 'none',
                          '&:hover': !role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions') ? {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 1px 3px ${alpha(theme.palette.primary.main, 0.2)}`
                          } : {},
                          '&:active': !role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions') ? {
                            cursor: 'grabbing'
                          } : {}
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                              {role.display_name}
                            </Typography>
                            {role.description && (
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                                {role.description.length > 30 ? role.description.substring(0, 30) + '...' : role.description}
                              </Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {role.is_system_role && (
                              <Chip
                                label="System"
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.grey[600], 0.1),
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  fontSize: '0.6rem',
                                  height: 18
                                }}
                              />
                            )}
                            {!role.is_system_role && level > hierarchyLevel && role.hierarchy_level > hierarchyLevel && canEditPage('roles_permissions') && (
                              <Typography variant="caption" sx={{ 
                                color: 'text.secondary',
                                fontSize: '0.6rem',
                                fontStyle: 'italic'
                              }}>
                                Draggable
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                      ))
                  )}
                </Box>
              </Box>
            ))
          }
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, mt: 2 }}>
              Available Roles
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage user roles and permissions for your organization
            </Typography>
          </Box>
          {canEditPage('roles_permissions') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateRole}
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.2,
                fontSize: '0.9rem',
                fontWeight: 500,
                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                color: 'text.secondary',
                '&:hover': {
                  background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Create Custom Role
            </Button>
          )}
        </Box>

        {/* Filter Section */}
        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          mx: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Search Roles
              </Typography>
              <TextField
                fullWidth
                placeholder="Search by name, display name, or description..."
                value={roleSearchTerm}
                onChange={(e) => setRoleSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    height: 48,
                    '&:hover': {
                      border: `1px solid ${alpha(theme.palette.grey[600], 0.8)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${alpha(theme.palette.grey[700], 0.9)}`
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    py: 1.5,
                    px: 2
                  },
                  '& .MuiOutlinedInput-root': {
                    height: 48,
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{ width: 180 }}>
              <Typography variant="body2" sx={{ 
                mb: 1, 
                fontWeight: 500, 
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Hierarchy Level
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedHierarchyLevel}
                  onChange={(e) => setSelectedHierarchyLevel(e.target.value)}
                  displayEmpty
                  MenuProps={{
                    disableScrollLock: true
                  }}
                  sx={{
                    height: 48,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    '&:hover': {
                      border: `1px solid ${alpha(theme.palette.grey[600], 0.8)}`
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  }}
                >
                  <MenuItem value="">
                    <Typography sx={{ color: 'text.secondary' }}>All Levels</Typography>
                  </MenuItem>
                  {[...new Set(roles.map(role => role.hierarchy_level))]
                    .sort((a, b) => a - b)
                    .map(level => (
                      <MenuItem key={level} value={level.toString()}>
                        Level {level} {level === 1 ? '(Super Admin)' : ''}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={!roleSearchTerm && !selectedHierarchyLevel}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  height: 48,
                  minWidth: 90,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  color: 'text.secondary',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    color: 'primary.main'
                  },
                  '&:disabled': {
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                    color: alpha(theme.palette.grey[500], 0.7)
                  }
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ 
          flex: 1,
          maxHeight: '45vh', 
          overflowY: 'auto',
          pr: 2,
          px: 3,
          py: 2
        }}>
          {Object.entries(getFilteredRolesByHierarchy())
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([level, levelRoles]) => (
            <Box key={level} sx={{ mb: 3 }}>
              {/* Hierarchy Level Header */}
              <Paper 
                sx={{ 
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
                onClick={() => toggleLevelExpansion(parseInt(level))}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`Level ${level}`}
                      color={getRoleStatusColor(parseInt(level))}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      {getHierarchyLevelName(parseInt(level))}
                    </Typography>
                    <Chip
                      label={`${levelRoles.length} ${levelRoles.length === 1 ? 'Role' : 'Roles'}`}
                      size="small"
                      sx={{ 
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: 'info.main',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                  {expandedLevels[parseInt(level)] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
              </Paper>

              {/* Roles in this level */}
              {expandedLevels[parseInt(level)] && (
                <Box sx={{ pl: 2 }}>
                  {levelRoles.map((role) => (
                    <Card key={role.id} sx={{ 
                      p: 3, 
                      mb: 2,
                      borderRadius: 3,
                      border: `1px solid ${alpha('rgb(0,0,0)', 0.08)}`,
                      boxShadow: `0 2px 8px ${alpha('rgb(0,0,0)', 0.04)}`,
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: `0 4px 16px ${alpha('rgb(0,0,0)', 0.08)}`,
                        transform: 'translateX(8px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      },
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        bgcolor: getRoleStatusColor(parseInt(level)) === 'error' ? 'error.main' :
                                 getRoleStatusColor(parseInt(level)) === 'warning' ? 'warning.main' :
                                 getRoleStatusColor(parseInt(level)) === 'info' ? 'info.main' : 'success.main',
                        borderRadius: '3px 0 0 3px'
                      }
                    }}
                    onClick={() => !role.is_system_role && canEditPage('roles_permissions') && (hierarchyLevel < role.hierarchy_level) && loadRoleForEdit(role)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                              {role.display_name === 'Super Admin' ? 'Super Administrator' : role.display_name}
                            </Typography>
                            {role.is_system_role && (
                              <Chip
                                label="System"
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.grey[600], 0.1),
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            {role.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                Settings Access:
                              </Typography>
                              <Chip
                                label={role.hasSettingsViewPermission ? 'Yes' : 'No'}
                                color={role.hasSettingsViewPermission ? 'success' : 'default'}
                                size="small"
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        {!role.is_system_role && canDeletePage('roles_permissions') && (hierarchyLevel < role.hierarchy_level) && (
                          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRoleClick(role);
                              }}
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                '&:hover': {
                                  bgcolor: alpha(theme.palette.error.main, 0.15),
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <DeleteIcon sx={{ color: 'error.main', fontSize: '1rem' }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    {/* Add Level Dialog */}
    <Dialog 
      open={addLevelDialogOpen} 
      onClose={() => setAddLevelDialogOpen(false)}
      maxWidth="sm" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500,
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        color: 'primary.main'
      }}>
        Add New Hierarchy Level
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Create a new hierarchy level for role assignment. Level must be greater than your current level ({hierarchyLevel}).
          </Typography>
          
          <TextField
            fullWidth
            label="Level Number"
            type="number"
            value={newLevelNumber}
            onChange={(e) => setNewLevelNumber(e.target.value)}
            placeholder="Enter level number (e.g., 5, 6, 7...)"
            inputProps={{ min: hierarchyLevel + 1 }}
            sx={{ 
              '& .MuiInputLabel-root': { fontWeight: 500 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: alpha(theme.palette.grey[50], 0.8),
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: alpha(theme.palette.primary.main, 0.5)
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: '2px'
                }
              }
            }}
          />
          
          {isInvalidLevelNumber() && newLevelNumber && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                background: alpha(theme.palette.error.main, 0.05)
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Cannot enter 0 or negative numbers. Please enter a positive level number.
              </Typography>
            </Alert>
          )}
          
          {doesLevelExist() && !isInvalidLevelNumber() && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                background: alpha(theme.palette.error.main, 0.05)
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                Hierarchy level {newLevelNumber} already exists. Choose a different number.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setAddLevelDialogOpen(false)} 
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateLevel}
          disabled={!newLevelNumber || doesLevelExist() || isInvalidLevelNumber()}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(10px)',
            color: 'text.secondary',
            fontWeight: 500,
            px: 4,
            py: 1.2,
            '&:hover': { 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.main, 0.08)})`,
              transform: 'translateY(-1px)'
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.grey[400], 0.5),
              color: theme.palette.grey[600]
            }
          }}
        >
          Create Level
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Level Dialog */}
    <Dialog 
      open={deleteLevelDialogOpen} 
      onClose={() => setDeleteLevelDialogOpen(false)} 
      maxWidth="sm" 
      fullWidth 
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500, 
        color: 'error.main',
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
      }}>
        Delete Level {levelToDelete}
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
          Are you sure you want to delete <strong>Level {levelToDelete}</strong>?
        </Typography>
        
        {hasRolesInLevel(levelToDelete) && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              background: alpha(theme.palette.error.main, 0.05)
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
              Cannot delete level - {roles.filter(role => role.hierarchy_level === levelToDelete).length} role(s) exist in this level:
            </Typography>
            <Box sx={{ ml: 2 }}>
              {roles.filter(role => role.hierarchy_level === levelToDelete).map((role, index) => (
                <Typography key={role.id} variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  • {role.display_name}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: 'text.secondary' }}>
              Reassign these roles to another level if you want to delete this one.
            </Typography>
          </Alert>
        )}
        
        <Box sx={{
          p: 3,
          borderRadius: 2,
          background: alpha(theme.palette.error.main, 0.05),
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This action will permanently delete the hierarchy level and all roles within it. This cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setDeleteLevelDialogOpen(false)}
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteLevel} 
          variant="contained" 
          disabled={hasRolesInLevel(levelToDelete)} 
          color="error"
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 4,
            py: 1.2,
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.grey[400], 0.5),
              color: theme.palette.grey[600]
            }
          }}
        >
          Delete Level
        </Button>
      </DialogActions>
    </Dialog>

    {/* Create Role Disabled */}

    {/* Confirm Delete Role Dialog (mirrors UsersSettings.jsx) */}
    <Dialog 
      open={confirmDeleteRoleDialogOpen} 
      onClose={() => setConfirmDeleteRoleDialogOpen(false)}
      maxWidth="md" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500, 
        color: 'error.main',
        fontSize: '1.5rem',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        pb: 3,
        mb: 2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`
      }}>
        Delete Role
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 4 }}>
        <Typography sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 500 }}>
          Are you sure you want to delete the role <strong>{roleToDelete?.display_name}</strong>?
        </Typography>
        
        {usersAffectedByRoleDeletion.length > 0 && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              background: alpha(theme.palette.error.main, 0.05)
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, color: 'text.secondary' }}>
              Cannot delete role - {usersAffectedByRoleDeletion.length} user(s) are currently assigned:
            </Typography>
            <Box sx={{ ml: 2 }}>
              {usersAffectedByRoleDeletion.map((user, index) => (
                <Typography key={user.id} variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  • {user.profiles?.full_name || user.profiles?.first_name || `User ${index + 1}`}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: 'text.secondary' }}>
              Reassign these users to other roles first, then try deleting again.
            </Typography>
          </Alert>
        )}

        <Box sx={{
          p: 3,
          borderRadius: 2,
          background: alpha(theme.palette.error.main, 0.05),
          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
        }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            This action will permanently delete the role and all its permissions. This cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.grey[50], 0.5),
        gap: 2
      }}>
        <Button 
          onClick={() => setConfirmDeleteRoleDialogOpen(false)}
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            '&:hover': {
              background: alpha(theme.palette.grey[400], 0.1)
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteRole}
          disabled={usersAffectedByRoleDeletion.length > 0}
          sx={{ 
            fontWeight: 500,
            borderRadius: 2,
            px: 4,
            py: 1.2,
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            },
            '&:disabled': {
              backgroundColor: alpha(theme.palette.grey[400], 0.5),
              color: theme.palette.grey[600]
            }
          }}
        >
          Delete Role
        </Button>
      </DialogActions>
    </Dialog>

    {/* Create Role Dialog */}
    <CreateRoleDialogue
      open={createRoleDialogOpen}
      onClose={() => setCreateRoleDialogOpen(false)}
      permissions={permissions}
      onCreateRole={handleConfirmCreateRole}
    />

    {/* Edit Role Dialog */}
    <CreateRoleDialogue
      open={editRoleDialogOpen}
      onClose={() => {
        setEditRoleDialogOpen(false);
        setRoleToEdit(null);
      }}
      permissions={permissions}
      editingRole={roleToEdit}
      onUpdateRole={handleUpdateRole}
    />
    </>
  );
}